import { Immutable } from "immer";
import React from "react";

import type { InsideInnovationBlock as Value } from "@reactivated";

type Props = Immutable<{
    value: Value;
}>;

/**
 * React port of the pillows-specific ``InsideInnovationBlock``
 * (defined in ``server/cms/components/ads.py``). DOM and class names
 * mirror the inline markup that lived in
 * ``cms/product_filter_grid_page.html`` (lines ~127-167 of the legacy
 * template before the F5 cutover): three "callout" rows (icon + title +
 * copy) alongside a larger callout image. The four ``*_copy`` fields are
 * authored as CharBlocks but rendered through Django's ``|safe`` filter,
 * so we preserve embedded inline HTML by setting innerHTML directly
 * (trust boundary is the CMS, same as the legacy template).
 */
export const InsideInnovationBlock = (props: Props): React.JSX.Element => {
    const v = props.value;
    const callouts = [
        {
            icon: v.callout1_icon,
            title: v.callout1_title,
            copy: v.callout1_copy,
        },
        {
            icon: v.callout2_icon,
            title: v.callout2_title,
            copy: v.callout2_copy,
        },
        {
            icon: v.callout3_icon,
            title: v.callout3_title,
            copy: v.callout3_copy,
        },
    ];
    return (
        <div className="collection-detail l-full-width">
            <div className="collection-detail__container l-capped-width">
                <div className="collection-detail-copy">
                    <div className="collection-detail-copy__title">
                        {v.title}
                    </div>
                    <div
                        className="collection-detail-copy__overview"
                        dangerouslySetInnerHTML={{ __html: v.copy ?? "" }}
                    />
                    <ul className="collection-detail-list">
                        {callouts.map((callout, i) => (
                            <li key={i} className="collection-detail-list-item">
                                <div className="collection-detail-list-item__icon-container">
                                    <img
                                        src={callout.icon?.url ?? ""}
                                        alt=""
                                        className="collection-detail-list-item__icon responsive-img"
                                        loading="lazy"
                                        decoding="async"
                                    />
                                </div>
                                <div className="collection-detail-list-item__copy">
                                    <div className="collection-detail-list-item__title">
                                        {callout.title}
                                    </div>
                                    <div
                                        className="collection-detail-list-item__description"
                                        dangerouslySetInnerHTML={{
                                            __html: callout.copy ?? "",
                                        }}
                                    />
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="collection-detail__image-container">
                    <img
                        src={v.callout_image?.url ?? ""}
                        alt=""
                        className="collection-detail__image"
                        loading="lazy"
                        decoding="async"
                    />
                </div>
            </div>
        </div>
    );
};
